import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminRole, isSuperAdmin } from '../services/userProfile';
import ProtectedAuthGate from '../pages/login/ProtectedAuthGate';

/**
 * Route gate for signed-in users.
 * Waits until Firebase Auth + users/{uid} profile are ready before role checks
 * (avoids first-navigation redirects/blanks while role is still null).
 * admin / superAdmin satisfy partner and gameMaker requirements.
 * Guests stay on this URL and get the auth modal — do not bounce to /login.
 */
const ProtectedRoute = ({ children, requiredRole }) => {
  const { currentUser, role, userProfile, authLoading, profileReady } = useAuth();

  const waitingForProfile = Boolean(currentUser) && !profileReady;
  if (authLoading || waitingForProfile) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', fontSize: '1.1rem', color: '#666',
      }}>
        Authenticating...
      </div>
    );
  }

  if (!currentUser) {
    return <ProtectedAuthGate />;
  }

  if (userProfile?.disabled) {
    return <ProtectedAuthGate disabled />;
  }

  if (requiredRole) {
    const hasAccess =
      isAdminRole(role) ||
      isSuperAdmin({ role, email: currentUser?.email }) ||
      (requiredRole === 'partner' && role === 'partner') ||
      (requiredRole === 'gameMaker' && role === 'gameMaker');
    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;
